// Repair ticket detail — /repair/:id
// Tabs: Thông tin | Phụ tùng | Nhân công | RMA | Trạng thái
import { useState } from "react";
import { useParams, useNavigate } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Printer, ChevronLeft, Trash2 } from "lucide-react";
import { useAuthStore } from "@/lib/auth/store";
import { toast } from "@/components/ui/toast";
import { translateApiError } from "@/lib/errors/error-map";
import { repairApi, STATUS_LABELS, STATUS_COLORS, ALLOWED_TRANSITIONS, fmtVND, fmtDate } from "./repair-api";
import { PartsSection } from "./components/parts-section";
import { LaborSection } from "./components/labor-section";
import { RMASection } from "./components/rma-section";
import { StatusTimeline } from "./components/status-timeline";

type Tab = "info" | "parts" | "labor" | "rma" | "timeline";

const TABS: { key: Tab; label: string }[] = [
  { key: "info",     label: "Thông tin" },
  { key: "parts",    label: "Phụ tùng" },
  { key: "labor",    label: "Nhân công" },
  { key: "rma",      label: "RMA" },
  { key: "timeline", label: "Trạng thái" },
];

export function RepairDetailPage() {
  // strict:false — route id that la "/protected/repair/$id" (layout cha co id
  // "protected"); dung from se throw khi render → trang trang.
  const { id } = useParams({ strict: false }) as { id: string };
  const user = useAuthStore((st) => st.user);
  const canDelete = user?.role === "admin" || user?.role === "manager";
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [tab, setTab] = useState<Tab>("info");
  const [wsId, setWsId] = useState("");

  const { data, isLoading } = useQuery({
    queryKey: ["repair-ticket", id],
    queryFn: () => repairApi.getTicket(id),
  });

  const transition = useMutation({
    mutationFn: (s: string) => repairApi.transition(id, s),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["repair-ticket", id] }),
  });

  const print = useMutation({
    mutationFn: () => repairApi.print(id, wsId),
  });

  const del = useMutation({
    mutationFn: () => repairApi.deleteTicket(id),
    onSuccess: () => {
      toast.success("Đã xóa phiếu sửa chữa");
      navigate({ to: "/repair" });
    },
    onError: (e) => toast.error(translateApiError(e)),
  });

  if (isLoading) return <div className="py-16 text-center text-muted-foreground">Đang tải...</div>;
  if (!data) return <div className="py-16 text-center text-destructive">Không tìm thấy phiếu sửa</div>;

  const { ticket, parts, labor, rMAs } = data;
  const allowed = ALLOWED_TRANSITIONS[ticket.status] ?? [];
  const isTerminal = ["delivered", "cancelled"].includes(ticket.status);

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      {/* Back + header */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <button onClick={() => navigate({ to: "/repair" })} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-1">
            <ChevronLeft size={14} /> Danh sách sửa chữa
          </button>
          <h1 className="text-2xl font-bold font-mono">{ticket.code}</h1>
          <p className="text-sm text-muted-foreground">Tiếp nhận: {fmtDate(ticket.receivedAt)}</p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {/* Status badge */}
          <span className={`rounded-full px-3 py-1 text-xs font-medium ${STATUS_COLORS[ticket.status] ?? ""}`}>
            {STATUS_LABELS[ticket.status] ?? ticket.status}
          </span>

          {canDelete && (
            <button
              onClick={() => {
                if (window.confirm(`Xóa hẳn phiếu ${ticket.code}?`)) del.mutate();
              }}
              disabled={del.isPending}
              className="flex items-center gap-1 rounded-md border border-destructive/40 px-3 py-1.5 text-sm text-destructive hover:bg-destructive/5 disabled:opacity-40"
            >
              <Trash2 size={14} /> Xóa
            </button>
          )}

          {/* Transition dropdown */}
          {allowed.length > 0 && (
            <select
              className="rounded-md border bg-background px-3 py-1.5 text-sm focus:outline-none"
              defaultValue=""
              onChange={(e) => { if (e.target.value) transition.mutate(e.target.value); e.target.value = ""; }}
              disabled={transition.isPending}
            >
              <option value="" disabled>Chuyển trạng thái...</option>
              {allowed.map((s) => (
                <option key={s} value={s}>{STATUS_LABELS[s] ?? s}</option>
              ))}
            </select>
          )}

          {/* Print */}
          <div className="flex items-center gap-1">
            <input
              type="text"
              placeholder="Workstation ID"
              className="rounded-md border bg-background px-2 py-1.5 text-xs w-28 focus:outline-none"
              value={wsId}
              onChange={(e) => setWsId(e.target.value)}
            />
            <button
              onClick={() => print.mutate()}
              disabled={!wsId || print.isPending}
              className="flex items-center gap-1 rounded-md border px-3 py-1.5 text-sm hover:bg-accent disabled:opacity-40"
              title="In phiếu sửa chữa"
            >
              <Printer size={14} />
              {print.isPending ? "Đang in..." : "In phiếu"}
            </button>
          </div>
        </div>
      </div>

      {/* Errors */}
      {transition.isError && (
        <div className="rounded-md bg-destructive/10 px-4 py-2 text-sm text-destructive">
          Không thể chuyển trạng thái. Vui lòng thử lại.
        </div>
      )}

      {/* Tabs */}
      <div className="border-b flex gap-1">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 -mb-px ${
              tab === t.key
                ? "border-primary text-primary"
                : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >
            {t.label}
            {t.key === "parts" && parts.length > 0 && (
              <span className="ml-1 rounded-full bg-muted px-1 text-xs">{parts.length}</span>
            )}
            {t.key === "labor" && labor.length > 0 && (
              <span className="ml-1 rounded-full bg-muted px-1 text-xs">{labor.length}</span>
            )}
            {t.key === "rma" && rMAs.length > 0 && (
              <span className="ml-1 rounded-full bg-muted px-1 text-xs">{rMAs.length}</span>
            )}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div className="rounded-xl border bg-card p-5">
        {tab === "info" && (
          <div className="grid gap-5 md:grid-cols-2">
            <div className="space-y-3">
              <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">Thiết bị</h3>
              <Row label="Thiết bị" value={ticket.deviceDesc} />
              {ticket.serialNo && <Row label="Serial" value={ticket.serialNo} mono />}
              {ticket.accessories && <Row label="Phụ kiện kèm" value={ticket.accessories} />}
              <Row label="Mô tả lỗi" value={ticket.issueDesc} />
              {ticket.diagnosis && <Row label="Chẩn đoán" value={ticket.diagnosis} />}
            </div>
            <div className="space-y-3">
              <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">Chi phí</h3>
              <Row label="Ước tính" value={`${fmtVND(ticket.estimatedCost)}₫`} />
              <Row label="Phụ tùng" value={`${fmtVND(ticket.partsCost)}₫`} />
              <Row label="Nhân công" value={`${fmtVND(ticket.laborCost)}₫`} />
              <Row label="Tổng cộng" value={`${fmtVND(ticket.finalCost)}₫`} bold />
              <Row label="Đã thu" value={`${fmtVND(ticket.paidAmount)}₫`} />
              {ticket.deliveredAt && <Row label="Giao lúc" value={fmtDate(ticket.deliveredAt)} />}
            </div>
            <div className="space-y-3 md:col-span-2">
              <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">Khách hàng</h3>
              <Row label="Tên khách" value={ticket.customerName ?? "—"} />
              {ticket.customerPhone && <Row label="Điện thoại" value={ticket.customerPhone} mono />}
              {ticket.technicianEmail && <Row label="Kỹ thuật viên" value={ticket.technicianEmail} />}
            </div>
          </div>
        )}

        {tab === "parts" && (
          <PartsSection ticketId={id} parts={parts} disabled={isTerminal} />
        )}
        {tab === "labor" && (
          <LaborSection ticketId={id} labor={labor} disabled={isTerminal} />
        )}
        {tab === "rma" && (
          <RMASection ticketId={id} serialId={ticket.serialId} rmas={rMAs} />
        )}
        {tab === "timeline" && (
          <StatusTimeline ticket={ticket} />
        )}
      </div>
    </div>
  );
}

function Row({ label, value, mono, bold }: { label: string; value: string; mono?: boolean; bold?: boolean }) {
  return (
    <div className="flex justify-between gap-3 text-sm">
      <span className="text-muted-foreground shrink-0">{label}</span>
      <span className={`text-right ${mono ? "font-mono" : ""} ${bold ? "font-semibold" : "font-medium"}`}>
        {value}
      </span>
    </div>
  );
}
